/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, RefAttributes } from 'react'
import { RowKeyType, Cell as RsuiteCell } from 'rsuite-table'
import { InnerCellProps } from 'rsuite-table/lib/Cell'
import { Whisper, Tooltip } from 'rsuite'

type CellChildren =
  | React.ReactNode
  | ((rowData: any, rowIndex?: number) => React.ReactNode)

type Props = Omit<InnerCellProps<any, RowKeyType>, 'children'> &
  RefAttributes<HTMLDivElement> & {
    children?: CellChildren
    formatTooltip?: (rawValue: unknown) => string | null
  }

const getTooltipText = (
  children: CellChildren,
  rawValue: unknown
): string | null => {
  if (
    typeof children === 'function' ||
    React.isValidElement(children) ||
    Array.isArray(children)
  ) {
    return rawValue != null && rawValue !== '' ? String(rawValue) : null
  }
  if (children != null && children !== '') return String(children)
  return rawValue != null && rawValue !== '' ? String(rawValue) : null
}

const Cell: FC<Props> = ({
  dataKey,
  rowData,
  children,
  formatTooltip,
  ...props
}: Props) => {
  const rawValue = dataKey ? rowData?.[dataKey] : undefined
  const fallbackDisplay = rawValue != null && rawValue !== '' ? rawValue : '-'
  const displayChildren = children != null ? children : fallbackDisplay
  const tooltipText = formatTooltip
    ? formatTooltip(rawValue)
    : getTooltipText(children, rawValue)

  return (
    <Whisper
      placement="bottom"
      trigger={tooltipText ? 'hover' : 'none'}
      speaker={<Tooltip>{tooltipText}</Tooltip>}
    >
      <RsuiteCell
        dataKey={dataKey}
        rowData={rowData}
        {...(props as any)}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...(props as any).style
        }}
      >
        {displayChildren as React.ReactNode}
      </RsuiteCell>
    </Whisper>
  )
}

export default Cell
